import React, { useEffect, useState, createRef, useRef } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Button,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  assignPostAcqTasksTo,
  loadPostAcqTasks,
  closePostAcqTasks,
  openPostAcqTasks,
  deletePostAcqTasks,
  unassignPostAcqTasks,
  editPostAcqTasks,
  setConfirmationModalState,
  setPostAcqWorkflowLoadingOverlayState,
  setIsFetchingPostAcqWorkflowData,
  createExploreSystemView,
  appNotify,
} from 'store/actions';
import { push } from 'connected-react-router';
import { Stack, LoadingOverlayWrapper } from 'components/layout';
import { DeleteModal } from 'components/underwrite/shared/workflow';
import PostAcqWorkflowGrid from 'components/underwrite/postAcquisition/postAcqWorkflow/PostAcqWorkflowGrid';
import { Constants } from 'utilities/constants';
import { sendClientLog } from 'services/logService';
import { format as dateFormat } from 'date-fns';
import dataGridExtension from 'components/devExtreme/dataGridExtension';
import LayoutPopover from 'components/underwrite/acquisition/channelWorkflow/LayoutPopover';
import AssignModal from 'components/underwrite/shared/workflow/AssignModal';
import EditModal from 'components/underwrite/shared/workflow/EditModal';
import LoadLogModal from 'components/underwrite/postAcquisition/postAcqWorkflow/LoadLogModal';

const gridRef = createRef();

const PostAcqWorkflow = ({
  classes,
  assignPostAcqTasksTo,
  workflowGridNeedsToRefresh,
  closePostAcqTasks,
  openPostAcqTasks,
  deletePostAcqTasks,
  editPostAcqTasks,
  isFetchingClientTxnOppsData,
  channelWorkflowLoadingOverlayState,
  match,
  loadPostAcqTasks,
  setConfirmationModalState,
  setIsFetchingPostAcqWorkflowData,
  setPostAcqWorkflowLoadingOverlayState,
  unassignPostAcqTasks,
  user,
  push,
  createExploreSystemView,
  appNotify,
}) => {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editField, setEditField] = useState(null);
  const [isLoadLogModalOpen, setIsLoadLogModalOpen] = useState(false);
  const [loadLogTaskId, setLoadLogTaskId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [gridKey, setGridKey] = useState(0);

  const isVisited = useRef(false);
  useEffect(() => {
    if (match) isVisited.current = true;
  }, [match]); 

  function handleDataRefreshPressed(){
    setGridKey(gridKey+1);
    sendClientLog('Info', 'Post-Acq Workflow - User started data fetch - workflow refresh button');
  }

  /**Assign task */
  function handleOpenAssign(selectedRows) {
    setSelectedRows(selectedRows);
    setIsAssignModalOpen(true);
  }

  function assignTasks(type, selectedRows, selectedUserId) {
    const idsToAssign = selectedRows.map(opp => opp.id);
    assignPostAcqTasksTo(type, idsToAssign, selectedUserId, user.id); // Assign all tasks, whether unassigned or currently assigned to a user
    if (isAssignModalOpen) setIsAssignModalOpen(false);
  }

  /**Unassign task */
  function handleUnassign(selectedRows) {
    unassignPostAcqTasks('postAcqTask', selectedRows.map(opp => opp.id), user.id);
  }

  /**Edit task */
  function handleOpenEdit(selectedRows, field) {
    setSelectedRows(selectedRows);
    setEditField(field);
    setIsEditModalOpen(true);
  }

  function editTasks(type, rowsToUpdate, newValue) {
    const idsToEdit = rowsToUpdate.map(opp => opp.id);
    if (idsToEdit.length) {
      editPostAcqTasks(idsToEdit, editField, newValue);
    } else {
      // appNotify('No tasks were updated with a new value', 'warn');
    }
    if (isEditModalOpen) setIsEditModalOpen(false);
  }

  /**Reload Task Data */
  function handleLoadTasks(selectedRows) {
    setSelectedRows(selectedRows);
    let verb = '';
    if (!selectedRows.some(row => row.postAcquisitionLoadCompletedOn)) {
      verb = 'load';
    } else if (selectedRows.every(row => row.postAcquisitionLoadCompletedOn)) {
      verb = 'reload';
    } else {
      verb = 'load and reload';
    }
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleLoadTasksConfirmation(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to ${verb} data for ${selectedRows.length} ${selectedRows.length > 1 ? 'tasks' : 'task'}?`
    });
  }

  function handleLoadTasksConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    loadPostAcqTasks(selectedRows.map(row => row.id));
  }

  /**View Load Log */
  function handleViewLoadLog(selectedRows) {
    // The context menu only enables this when every selected task's most recent load is the same one, so any row identifies it
    setLoadLogTaskId(selectedRows[0].id);
    setIsLoadLogModalOpen(true);
  }

  /**Close Tasks */
  function handleCloseTasks(selectedRows) {
    setSelectedRows(selectedRows);
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleCloseTasksConfirmation(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to close and submit ${selectedRows.length} ${selectedRows.length > 1 ? 'tasks' : 'task'}?`
    });
  }

  function handleCloseTasksConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    closePostAcqTasks(selectedRows.map(row => row.id));
  }

  /**Open Tasks */
  function handleOpenTasks(selectedRows) {
    setSelectedRows(selectedRows);
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleOpenTasksConfirmation(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to reopen ${selectedRows.length} ${selectedRows.length > 1 ? 'tasks' : 'task'}?`
    });
  }

  function handleOpenTasksConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    openPostAcqTasks(selectedRows.map(row => row.id));
  }

  /**Delete Tasks */
  function handleDeleteTasks(selectedRows) {
    setSelectedRows(selectedRows);
    setIsDeleteModalOpen(true);
  }

  /**View on Explore */
  function handleViewOnExplore(selectedRows) {
    const asgPropIDs = [];
    selectedRows.forEach(row => {
      if (row.asgPropID && !asgPropIDs.includes(row.asgPropID)) {
        asgPropIDs.push(row.asgPropID);
      }
    });
    // construct filterValue
    const filterValue = ['asgPropID', 'in', asgPropIDs];
    // construct filterExpression to display on UI
    const filterExpression = [];
    asgPropIDs.forEach(id => {
      filterExpression.push(['asgPropID', '=', id]);
      filterExpression.push('or');
    });
    filterExpression.pop();
    const newFilter = { filterValue, filterExpression };
    createExploreSystemView(`Workflow opportunities`, newFilter);
    push(Constants.Routes.EXPLORE);
  }

  function deleteTasks() {
    const idsToDelete = selectedRows.map(opp => opp.id);
    deletePostAcqTasks(idsToDelete);
    if (isDeleteModalOpen) setIsDeleteModalOpen(false);
  }

  /**Shared method to close confirmation modal */
  function closeConfirmationModal() {
    setConfirmationModalState({ isModalOpen: false });
  }

  //#endregion

  function handleExcelExport() {
    setPostAcqWorkflowLoadingOverlayState({ isLoading: true, message: 'Exporting...' });

    const maxExportCount = 20000;
    const visibleRowsCount = gridRef.current.instance.totalCount()
    if(visibleRowsCount > maxExportCount) {
      appNotify(`Export is limited to ${maxExportCount.toLocaleString()} rows.`, 'error');
    } else {
      const timestamp = dateFormat(new Date(), '_yyyy-MM-dd_HHmmss');
      const fileName = 'ReVUe_WorkflowExport' + timestamp;
      const sheetName = 'Main Sheet';
      const customizeCell = options => {
        const { gridCell, excelCell } = options;
        const { column } = gridCell;
        // wrap text in column headers
        if (gridCell.rowType === "header") {
          excelCell.alignment = {
            horizontal: "center",
            vertical: "top",
            wrapText: true
          };
        }
        // display emoji for boolean values
        if (column.dataType === "boolean" && gridCell.rowType === "data") {
          excelCell.value === true
          ? excelCell.value = column.trueText
          : excelCell.value = column.falseText;
        }
      }
      dataGridExtension.exportToExcel(gridRef, fileName, sheetName, customizeCell);
    }

    setPostAcqWorkflowLoadingOverlayState(false, null);
    sendClientLog('Info', 'Post-Acq Workflow - User clicked all workflow column Excel export');
  }

  return ((match || isVisited.current) &&
    <Stack className={classes.root}style={!match // Channel Workflow is always mounted. Use CSS to hide when viewing other routes
      ? { position: 'absolute', left: '-100vw', visibility: 'hidden' }
      : null
    }>
      <div className={classes.headerContainer}>
        <Typography variant="h5">Post-Acq Workflow</Typography>
        <LayoutPopover
          gridRef={gridRef}
        />
        <div style={{ flexGrow: 1 }} />
        <Tooltip title='Refresh Grid Data'>
          <Badge
            invisible={isFetchingClientTxnOppsData || !workflowGridNeedsToRefresh}
            classes={{ colorPrimary: classes.colorPrimary, badge: classes.refreshBadge }}
            color='primary'
            variant="dot"
            overlap='rectangular'
          >
            <IconButton disabled={isFetchingClientTxnOppsData} onClick={handleDataRefreshPressed}>
              <RefreshIcon />
            </IconButton>
          </Badge>
        </Tooltip>
        <Tooltip title="Export visible data to Excel">
          <Button
            variant='contained'
            color='primary'
            onClick={handleExcelExport}
            style={{ marginLeft: 8 }}
          >
            Export
          </Button>
        </Tooltip>
      </div>
      <LoadingOverlayWrapper
        active={isFetchingClientTxnOppsData || (channelWorkflowLoadingOverlayState && channelWorkflowLoadingOverlayState.isLoading)}
        message={isFetchingClientTxnOppsData ? 'Fetching task data...' : channelWorkflowLoadingOverlayState.message}
      >
        <Paper className={classes.channelWorkflowPaper}>
          <PostAcqWorkflowGrid
            ref={gridRef}
            updateGrid={gridKey}
            assignTasks={assignTasks}
            handleDeleteTasks={handleDeleteTasks}
            handleCloseTasks={handleCloseTasks}
            handleOpenTasks={handleOpenTasks}
            handleLoadTasks={handleLoadTasks}
            handleViewLoadLog={handleViewLoadLog}
            handleOpenAssign={handleOpenAssign}
            handleUnassign={handleUnassign}
            handleViewOnExplore={handleViewOnExplore}
            handleEditTask={handleOpenEdit}
            setGridLoading={setIsFetchingPostAcqWorkflowData}
          />
          <DeleteModal
            module={Constants.Module.POSTACQ}
            selectedRows={selectedRows}
            isOpen={isDeleteModalOpen}
            setIsOpen={setIsDeleteModalOpen}
            deleteItems={deleteTasks}
          />
          <AssignModal
            module={Constants.Module.POSTACQ}
            selectedRows={selectedRows}
            isOpen={isAssignModalOpen}
            setIsOpen={setIsAssignModalOpen}
            assignTasks={assignTasks}
          />
          <EditModal
            module={Constants.Module.POSTACQ}
            selectedRows={selectedRows}
            isOpen={isEditModalOpen}
            setIsOpen={setIsEditModalOpen}
            editField={editField}
            editTasks={editTasks}
          />
          <LoadLogModal
            isOpen={isLoadLogModalOpen}
            setIsOpen={setIsLoadLogModalOpen}
            taskId={loadLogTaskId}
          />
        </Paper>
      </LoadingOverlayWrapper>
    </Stack>
  );
};

const styles = theme => ({
  root: {
    width: '100%',
    height: '100%',
    padding: '0px 48px'
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '8px 0'
  },
  refreshBadge: {
    marginTop: 8,
    marginRight: 8
  },
  colorPrimary: {
    backgroundColor: 'Red', //'#8c8c8c', '#999999'
    color: 'White',
  },
  toggle: {
    marginRight: 0
  },
  channelWorkflowPaper: {
    flexGrow: 1
  }
});

const mapState = state => {
  return {
    user: state.appState.user,
    workflowGridNeedsToRefresh: state.postAcqWorkflowState.workflowGridNeedsToRefresh,
    channelWorkflowLoadingOverlayState: state.postAcqWorkflowState.channelWorkflowLoadingOverlayState,
  }
};
const mapDispatch = {
  assignPostAcqTasksTo,
  loadPostAcqTasks,
  closePostAcqTasks,
  openPostAcqTasks,
  deletePostAcqTasks,
  unassignPostAcqTasks,
  setConfirmationModalState,
  setPostAcqWorkflowLoadingOverlayState,
  createExploreSystemView,
  push,
  appNotify,
  setIsFetchingPostAcqWorkflowData,
  editPostAcqTasks
};

export default compose(
  withStyles(styles),
  connect(
    mapState,
    mapDispatch
  )
)(PostAcqWorkflow);
